/**
 * Portal Theme Composable
 *
 * Bridges clientportal portal branding to the shared theme system. Reads
 * `brandColor1` from the location store and calls `useTheme().setBrandTokens`
 * so the full CP theme — color scales (50-900), contrast colors, and the
 * HighRise `--primary-*` / `--error-*` bridge — is generated from the portal's
 * brand color.
 *
 * Mounted once in the persistent clientportal root shell (`CPRootContainer`).
 * Community group routes render inside the same app and own the theme via
 * `useCommunityTheme`, which calls `resetBrandTokens()` on unmount. So this
 * composable must RE-ASSERT the portal theme — not apply once — whenever:
 *   - the brand color resolves,
 *   - the route leaves a community back to a clientportal page, or
 *   - the active tokens get cleared by the community reset.
 * `brandColor1` → primary and `brandColor2` → secondary (see `mapPortalBrandingToTokens`).
 */

import { watch } from 'vue'
import { useRoute } from 'nuxt/app'

import { useTheme } from '@common/theme-layer/composables/useTheme'
import type { BrandTokens } from '@common/theme-layer/composables/useTheme'

import { useLocationStore } from '../store/location.store'
import { mapPortalBrandingToTokens } from '../utils/portal-brand-tokens'

const COMMUNITY_GROUP_ROUTE_PREFIX = 'communities-groups'

export const usePortalTheme = () => {
  const { activeBrandTokens, setBrandTokens } = useTheme()
  const locationStore = useLocationStore()
  const route = useRoute()

  const isCommunityGroupRoute = () => typeof route.name === 'string' && route.name.startsWith(COMMUNITY_GROUP_ROUTE_PREFIX)

  const applyPortalTheme = () => {
    // Community routes own the theme via useCommunityTheme — don't fight it there.
    if (isCommunityGroupRoute()) {
      return
    }

    const tokens = mapPortalBrandingToTokens(locationStore.branding)

    if (!tokens.primary) {
      return
    }

    // Idempotent: skip when every token we would set is already active. Compares
    // the full provided set (primary + tertiary when present) so a brandColor2
    // change re-applies, while still breaking the re-apply loop — setBrandTokens
    // mutates activeBrandTokens, which is a watched source.
    const active = activeBrandTokens.value
    const alreadyActive = !!active && (Object.keys(tokens) as (keyof BrandTokens)[]).every(key => active[key] === tokens[key])

    if (alreadyActive) {
      return
    }

    setBrandTokens(tokens)
  }

  // Single-mount in the persistent CPRootContainer root: the watcher lives for the
  // app-session lifetime and Vue disposes it on root teardown, so no manual stop is
  // needed. Do not reuse this composable in a non-root/remountable context without
  // adding teardown.
  watch(
    () => [
      locationStore.branding.brandColor1,
      locationStore.branding.brandColor2,
      route.name,
      activeBrandTokens.value?.primary,
      activeBrandTokens.value?.secondary,
    ],
    applyPortalTheme,
    { immediate: true }
  )

  return { applyPortalTheme }
}
